<template>
    <v-app>
        <v-main class="main">

            <!-- Sidebar -->
            <AdminSidebar :drawerOpen="drawerOpen" :collapsed="isSidebarCollapsed" :isMobile="isMobile"
                @update:drawerOpen="drawerOpen = $event" />

            <!-- Main Content -->
            <v-container fluid class="main-content pa-4">

                <!-- Header / Summary Section -->
                <Header :isDarkMode="isDarkMode" :isMobile="isMobile" @toggle-sidebar="toggleSidebar"
                    @toggle-darkmode="toggleDarkMode" />

                <h2 class="report-title mt-4">Investment Report</h2>

                <!-- Quick Stats / KPI Cards -->
                <v-row class="stats mt-4" dense>
                    <v-col cols="12" sm="6" md="3">
                        <StatCard title="Total Investments" :value="formatCurrency(totalInvestment)" icon="mdi-transfer"
                            iconColor="#64cf69" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <StatCard title="Total Withdrawals" :value="formatCurrency(totalWithdrawal)"
                            icon="mdi-cash-minus" iconColor="#EF5350" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <StatCard title="Active Products" :value="totalProducts" icon="mdi-briefcase-outline"
                            iconColor="#64cf69" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <StatCard title="Wallet Balance" :value="formatCurrency(walletBalance)"
                            icon="mdi-wallet-outline" iconColor="#64cf69" />
                    </v-col>
                </v-row>

                <!-- Detailed Table / Logs -->
                <v-card class="table-card mt-6">
                    <div id="print-section">
                        <h3>Investment Report Log</h3>

                        <div class="table-header flex justify-between align-center flex-wrap">
                            <!-- Left side: search + clear -->
                            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 8px;">
                            
                            </div>

                            <!-- Right side: Add Product -->
                           <div class="mb-3">
                                <Button label="Export CSV" icon="pi pi-file" class="p-button-success mr-2"
                                    @click="exportCSV" />
                                <Button label="Export PDF" icon="pi pi-file-pdf" class="theme-button mr-2"
                                    @click="exportPDF" />
                                <Button label="Print" icon="pi pi-print" class="p-button-warning"
                                    @click="printReport" />
                            </div>
                            
                        </div>

                        <DataTable v-model:filters="filters" :value="investments" paginator :rows="20" dataKey="id"
                            stripedRows class="modern-table" :rowsPerPageOptions="[5, 10, 20, 50]"
                            tableStyle="min-width: 50rem" filterDisplay="menu" @row-click="goToDetails"
                            selectionMode="single" :globalFilterFields="['product.name', 'profile.account_id', 'status']">

                            
                            <!-- Customer Column -->
                            <Column header="Customer">
                                <template #body="slotProps">
                                    <div class="product-cell">
                                        <div>
                                            <div class="product-name">{{ slotProps.data.user.profile.account_id }}</div>
                                            <!-- <div class="product-desc">{{ truncate(slotProps.data.user.profile.firstname) }} {{ truncate(slotProps.data.user.profile.lastname)[0] }}</div> -->
                                        </div>
                                    </div>
                                </template>
                            </Column>

                            <!-- Status Column -->
                            <Column header="Product">
                                <template #body="slotProps">
                                    <div class="product-name">{{ slotProps.data.product.name }}</div>
                                </template>
                            </Column>

                            <!-- Status Column -->
                            <Column header="Status">
                                <template #body="slotProps">
                                    <span :class="[
                                        'status-pill',
                                        slotProps.data.status === 'active'
                                            ? 'status-active'
                                            : 'status-inactive'
                                    ]">
                                        {{ slotProps.data.status === 'active' ? 'Active' : 'Inactive' }}
                                    </span>
                                </template>
                            </Column>

                            <!-- Status Column -->
                            <Column header="Amount">
                                <template #body="slotProps">
                                    {{ formatCurrency(slotProps.data.price) }}
                                </template>
                            </Column>

                            <!-- Status Column -->
                            <Column header="Interest">
                                <template #body="slotProps">
                                    <div class="">@ {{ parseFloat(slotProps.data.product.interest_rate) }}% p.a</div>
                                </template>
                            </Column>

                            <!-- Created Column -->
                            <Column header="Maturity Date">
                                <template #body="slotProps">
                                    {{ formatDate(slotProps.data.maturity_date) }}
                                </template>
                            </Column>

                            <!-- Yield Column -->
                            <Column header="Current Yield">
                                <template #body="slotProps">
                                    {{ formatCurrency(slotProps.data.accumulated_yield) }}
                                </template>
                            </Column>

                            <!-- Payout -->
                            <Column header="Expected Payout">
                                <template #body="slotProps">
                                    {{ formatCurrency(slotProps.data.expected_payout) }}
                                </template>
                            </Column>

                        </DataTable>
                    </div>
                    <!-- Totals at bottom -->
                    <div class="totals mt-4">
                        <strong>Total Investment:</strong> {{ formatCurrency(totalInvestment) }} |
                        <strong style="color:red">Pending Withdrawal:</strong> {{ formatCurrency(pendingWithdrawal) }} |
                        <strong style="color: green;">Succcesfull Withdrawal:</strong> {{ formatCurrency(totalWithdrawal) }}
                    </div>
                </v-card>

                 <!-- Charts Section -->
                <v-card class="large-box mt-6 pa-4">
                    <v-row>
                        <v-col cols="12" sm="3">
                            <h5>Investments vs Withdrawals</h5>
                            <Chart type="doughnut" :data="doughnutData" :options="chartOptions" />
                        </v-col>
                        <v-col cols="12" sm="9" class="chart-wrapper">
                            <h5>Monthly Trends</h5>
                            <Chart type="bar" :data="barData" :options="barOptions" style="height:400px" />
                        </v-col>
                    </v-row>
                </v-card>
                <v-card class="large-box mt-6 pa-4">
                    <v-row>
                        <v-col cols="12" sm="12" class="chart-wrapper">
                            <h5>Monthly Trends</h5>
                            <Chart type="line" :data="lineData" :options="lineOptions" style="height:400px" />
                        </v-col>
                    </v-row>
                </v-card>

            </v-container>

        </v-main>
    </v-app>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted } from "vue"
import Header from "@/components/Header.vue"
import AdminSidebar from "@/components/AdminSidebar.vue"
import StatCard from "@/components/StatCard.vue"
import Chart from "primevue/chart"
import DataTable from "primevue/datatable"
import Column from "primevue/column"
import Button from 'primevue/button'
import { useAuthStore } from "@/store/auth"
import { useInvStore } from "@/store/investment"
import { useTransactStore } from "@/store/transaction"
import { formatCurrency, formatMonthlyData, formatDate } from "@/util"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import * as XLSX from "xlsx"
import { saveAs } from "file-saver"

const auth = useAuthStore()

const isSidebarCollapsed = ref(false)
const isDarkMode = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const loading = ref(true)


// Filters / Controls
const dateFrom = ref(new Date().toISOString().slice(0, 10))
const dateTo = ref(new Date().toISOString().slice(0, 10))
const searchTerm = ref("")
const filters = ref({ global: { value: null, matchMode: "contains" } })

// Transactions data
const tr = useTransactStore()
const walletTr = computed(() => tr.walletTr || [])

// Summary KPIs
const stat = computed(() => auth.stat || {})
const totalInvestment = computed(() => stat.value?.totalInvestments || 0)
const totalWithdrawal = computed(() => walletTr.value?.totalWithdrawal?.filter(tr => tr.status === "successful" && tr.type === "withdrawal")
    .reduce((sum, tr) => sum + Number(tr.amount || 0), 0) || 0)
const totalProducts = computed(() => stat.value?.totalProducts || 0)
const walletBalance = computed(() => stat.value?.walletBalance || 0)
const pendingWithdrawal = computed(() => stat.value?.totalWithdrawal || 0)

const inv = useInvStore();
const investments = computed(() => inv.investments || [])





// Filtered transactions
const filteredInvestments = computed(() => {
    return investments.value.filter(item => {

        const itemDate = item.maturity_date
            ? new Date(item.maturity_date)
            : null

        // If no date on item, don't exclude it
        if (!itemDate) return true

        const from = dateFrom.value ? new Date(dateFrom.value) : null
        const to = dateTo.value ? new Date(dateTo.value) : null

        // Normalize time (VERY IMPORTANT)
        if (from) from.setHours(0, 0, 0, 0)
        if (to) to.setHours(23, 59, 59, 999)

        const matchesDate =
            (!from || itemDate >= from) &&
            (!to || itemDate <= to)

        const matchesSearch = searchTerm.value
            ? (item.user?.profile?.account_id || "")
                .toLowerCase()
                .includes(searchTerm.value.toLowerCase())
            : true

        return matchesDate && matchesSearch
    })
})

// Doughnut chart
const doughnutData = computed(() => ({
    labels: ["Investments", "Withdrawals"],
    datasets: [{
        data: [totalInvestment.value, totalWithdrawal.value],
        backgroundColor: ["#64cf69", "#EF5350"],
        hoverBackgroundColor: ["#81C784", "#E57373"]
    }]
}))

// Bar chart (monthly trends)
const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]



const barData = computed(() => {
    return {
        labels: months,
        datasets: [
            {
                label: 'Investments',
                data: formatMonthlyData(stat.value?.monthlyInvestments),
                backgroundColor: '#64cf69'
            },
            {
                label: 'Withdrawals',
                data: formatMonthlyData(stat.value?.monthlyWithdrawals),
                backgroundColor: '#EF5350'
            }
        ]
    }
})

const chartOptions = { plugins: { legend: { position: "bottom" } } }
const barOptions = {
    responsive: true,
    maintainAspectRatio: false, // crucial to allow dynamic height
    plugins: {
        legend: {
            position: "top"
        }
    },
    scales: {
        y: {
            beginAtZero: true
        }
    }
}

// Line chart (monthly trends)
const lineData = computed(() => ({
    labels: months,
    datasets: [
        {
            label: 'Investments',
            data: formatMonthlyData(stat.value?.monthlyInvestments),
            borderColor: '#64cf69',   // line color
            backgroundColor: '#64cf69', // point fill color
            fill: false,
            tension: 0.3               // smooth curve
        },
        {
            label: 'Withdrawals',
            data: formatMonthlyData(stat.value?.monthlyWithdrawals),
            borderColor: '#EF5350',
            backgroundColor: '#EF5350',
            fill: false,
            tension: 0.3
        }
    ]
}));

// Chart options
const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            position: "top"
        },
        tooltip: {
            mode: "index",
            intersect: false
        }
    },
    scales: {
        x: {
            title: {
                display: true,
                text: "Month"
            }
        },
        y: {
            beginAtZero: true,
            title: {
                display: true,
                text: "Amount"
            }
        }
    }
};


const toggleSidebar = () => {
    if (isMobile.value) {
        drawerOpen.value = !drawerOpen.value
    } else {
        isSidebarCollapsed.value = !isSidebarCollapsed.value
    }
}

const checkMobile = () => {
    isMobile.value = window.innerWidth < 768
    drawerOpen.value = !isMobile.value
}

// Export to pdf
const exportPDF = () => {
    const doc = new jsPDF()

    const tableData = investments.value.map(item => [
        item.user?.profile?.account_id,
        item.product?.name,
        item.status,
        item.price,
        item.accumulated_yield,
        item.expected_payout,
        formatDate(item.maturity_date)
    ])

    autoTable(doc, {
        head: [["Customer", "Product", "Status", "Amount", "Interest", "Payout", "Maturity"]],
        body: tableData
    })

    doc.save("investment-report.pdf")
}

//Export to CSV/Excel
const exportCSV = () => {
    // Map your data
    const data = investments.value.map(item => ({
        Customer: item.user?.profile?.account_id,
        Product: item.product?.name,
        Status: item.status,
        Amount: item.price,
        Interest: item.accumulated_yield,
        Payout: item.expected_payout,
        Maturity: formatDate(item.maturity_date)
    }));

    // Convert JSON to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Create a new workbook and append worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    // Export as CSV instead of XLSX
    const csvOutput = XLSX.write(workbook, {
        bookType: "csv",
        type: "array" // produces a Uint8Array
    });

    // Save as file
    const file = new Blob([csvOutput], { type: "text/csv;charset=utf-8;" });
    saveAs(file, "investment-report.csv");
}

const printReport = () => {
    const printContents = document.querySelector('.p-datatable').outerHTML;
    const originalContents = document.body.innerHTML;

    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload(); // reload Vue app
}

onMounted(async () => {
    checkMobile()
    window.addEventListener("resize", checkMobile)

    loading.value = true
    await auth.loadPartner()
    await auth.loadPartnerStat()
    await inv.loadInvestment()
    await tr.loadWalletTransaction()
    console.log("RAW:", investments.value.length)
    console.log("FILTERED:", filteredInvestments.value.length)

})
</script>

<style scoped>
.main {
    display: flex;
    min-height: 100vh;
}

.main-content {
    flex: 1;
    background: #f8fafc;
}

.large-box {
    min-height: 300px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    background: #fff;
}

/* Card */
.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

/* Header */
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

/* Table */
.modern-table {
  border: none;
}

/* Status */
.status-pill {
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
}

.status-active {
    background: #ecfdf5;
    color: #16a34a;
}

.status-inactive {
    background: #fef2f2;
    color: #dc2626;
}

.report-title {
    font-size: 24px;
    font-weight: 600;
}

.report-subtitle {
    font-size: 14px;
    color: #555;
    margin-bottom: 16px;
}

.totals {
    margin-top: 16px;
    font-size: 18px;
    font-weight: 600;
}

.chart-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.chart-wrapper canvas {
    width: 100% !important;
    /* stretch canvas full width */
    height: 300px !important;
    /* or whatever height you want */
}
.theme-button {
  height: 46px;
  background: #000;
  color: #fff;
  border-radius: 10px;
  font-weight: 600;
  transition: all 0.25s ease;
}
</style>
